import React, { useEffect, useState } from "react";
import MobileLayout from "../components/layout/MobileLayout";
import { api } from "../lib/api";
import { Dices, TrendingUp, TrendingDown, Rocket, Flame, Coins } from "lucide-react";

const GAME_META = {
  aviator:      { label: "AVIATOR",      icon: Rocket,  hue: "from-fuchsia-600 to-rose-600" },
  dragon_tiger: { label: "DRAGON TIGER", icon: Flame,   hue: "from-rose-700 to-amber-500" },
  coin_flip:    { label: "COIN FLIP",    icon: Coins,   hue: "from-amber-600 to-yellow-500" },
};

function statusMeta(b) {
  const s = (b.status || "").toLowerCase();
  if (s === "won") return { text: `+₹${b.payout ?? 0}`, cls: "text-emerald-200 bg-emerald-500/20 border-emerald-400/40" };
  if (s === "lost") return { text: `-₹${b.amount ?? 0}`, cls: "text-rose-200 bg-rose-500/20 border-rose-400/40" };
  return { text: "Pending", cls: "text-amber-200 bg-amber-500/20 border-amber-400/40" };
}

export default function CasinoHistoryPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/user/casino-history?limit=100")
      .then(({ data }) => setRows(Array.isArray(data) ? data : []))
      .finally(() => setLoading(false));
  }, []);

  const totals = rows.reduce(
    (a, b) => {
      const st = (b.status || "").toLowerCase();
      a.stake += b.amount || 0;
      if (st === "won") a.win += b.payout || 0;
      if (st === "lost") a.loss += b.amount || 0;
      return a;
    },
    { stake: 0, win: 0, loss: 0 }
  );

  return (
    <MobileLayout>
      <div className="px-3 pt-3 pb-6">
        <h1 className="text-lg font-black text-white flex items-center gap-2 mb-3">
          <Dices className="w-5 h-5 text-fuchsia-600" /> Casino History
        </h1>

        <div className="grid grid-cols-3 gap-2 mb-4">
          <Stat label="Stake" value={`₹${totals.stake}`} cls="from-slate-800 to-slate-900 text-amber-300" />
          <Stat label="Won"   value={`₹${totals.win}`}   cls="from-emerald-800 to-emerald-900 text-emerald-200" />
          <Stat label="Loss"  value={`₹${totals.loss}`}  cls="from-rose-800 to-rose-900 text-rose-200" />
        </div>

        {loading ? (
          <div className="text-center py-12 text-cyan-200/60 text-sm">Loading…</div>
        ) : !rows.length ? (
          <div className="text-center py-12 text-cyan-200/60 text-sm">Abhi tak koi casino bet nahi lagayi.</div>
        ) : (
          <div className="space-y-2">
            {rows.map((b, i) => {
              const meta = GAME_META[b.game_type] || { label: (b.game_type || "GAME").toUpperCase(), icon: Dices, hue: "from-slate-700 to-slate-900" };
              const st = statusMeta(b);
              const Icon = meta.icon;
              return (
                <div
                  key={b.id || i}
                  data-testid={`user-casino-row-${i}`}
                  className={`relative flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r ${meta.hue} text-white shadow-md overflow-hidden`}
                >
                  <div className="absolute inset-0 bg-black/30 pointer-events-none" />
                  <div className="relative flex-shrink-0 w-10 h-10 rounded-lg bg-white/15 flex items-center justify-center backdrop-blur-sm">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="relative flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-black tracking-wider uppercase bg-white/20 px-1.5 py-0.5 rounded">{meta.label}</span>
                    </div>
                    <div className="text-sm font-medium mt-0.5 truncate">
                      Pick: <span className="font-black">{b.pick || b.choice || b.side || "—"}</span>
                      {b.cashout_multiplier ? <span className="text-amber-300 ml-1">@ {Number(b.cashout_multiplier).toFixed(2)}x</span> : null}
                    </div>
                    <div className="text-[10px] text-white/70 mt-0.5">{new Date(b.created_at).toLocaleString("en-IN", { hour12: true })}</div>
                  </div>
                  <div className="relative text-right flex flex-col items-end gap-1">
                    <div className="text-[10px] text-white/80">Stake ₹{b.amount || 0}</div>
                    <div className={`text-xs font-black px-2 py-1 rounded-md border ${st.cls} flex items-center gap-1`}>
                      {(b.status || "").toLowerCase() === "won" ? <TrendingUp className="w-3 h-3" /> : (b.status || "").toLowerCase() === "lost" ? <TrendingDown className="w-3 h-3" /> : null}
                      {st.text}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </MobileLayout>
  );
}

function Stat({ label, value, cls }) {
  return (
    <div className={`rounded-xl p-2.5 bg-gradient-to-br ${cls} border border-white/10`}>
      <div className="text-[9px] uppercase tracking-widest opacity-80">{label}</div>
      <div className="text-sm font-black">{value}</div>
    </div>
  );
}
